/**
 * Helpers that turn raw Copilot chart rows into ChartCard `dataDefinition` pieces
 * (dimensions, metrics) and normalize cell values for rendering.
 *
 * Flow agent charts often use snake_case field names, compact dates (`YYYYMMDD`),
 * and loosely typed numerics. These helpers bridge that to Alvie ChartCard types.
 */

import type {
  ChartCardMetricType,
  Dimension,
  Metric,
  ChartDataRow,
  ChartDataValue
} from 'shared/components/ChartCard/types'
import { type CopilotChartType } from './types'
import type { AutocompleteString } from '@precis-digital/kurama/utils'

/** Synthetic dimension injected when a scatter chart has no real color/series field. */
export const COPILOT_SCATTER_SERIES_FIELD = '_copilot_series'
export const COPILOT_SCATTER_SERIES_VALUE = 'Points'

/** Series field written by forecast extraction (`history` | `forecast`). */
export const COPILOT_FORECAST_SERIES_FIELD = '_series'

const DATE_ISO_PATTERN = /^\d{4}-\d{2}-\d{2}$/
const DATE_COMPACT_PATTERN = /^\d{8}$/

const CURRENCY_FIELD_HINTS = ['revenue', 'cost', 'spend', 'budget'] as const
const PERCENTAGE_FIELD_HINTS = ['rate', 'share'] as const

/**
 * Resolves which field (if any) should drive ChartCard series / color encoding.
 * Forecast charts always use `_series`. Otherwise a non-empty `colorField` that
 * differs from `xField` is used; empty string means a single series.
 */
export const getEffectiveColorField = (chart: {
  colorField: string
  xField: string
  chartType?: CopilotChartType | AutocompleteString
}): string => {
  if (chart.chartType === 'forecast') {
    return COPILOT_FORECAST_SERIES_FIELD
  }

  const { colorField, xField } = chart
  return colorField.trim().length > 0 && colorField !== xField ? colorField : ''
}

/**
 * Converts Flow compact dates (`YYYYMMDD`) to ISO (`YYYY-MM-DD`).
 * Non-compact values are returned as strings unchanged.
 */
export const normalizeAgentDateValue = (value: unknown): string => {
  const text = String(value)
  if (DATE_COMPACT_PATTERN.test(text)) {
    return `${text.slice(0, 4)}-${text.slice(4, 6)}-${text.slice(6, 8)}`
  }
  return text
}

/**
 * Formats a date string for chart titles (e.g. intervention / forecast cutoff).
 * Falls back to the raw string when parsing fails.
 */
export const formatCompactDateLabel = (value: string): string => {
  const normalized = normalizeAgentDateValue(value)
  const date = new Date(normalized)
  if (Number.isNaN(date.getTime())) {
    return value
  }

  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  })
}

/** ChartCard dimension for the synthetic single scatter series. */
export const buildScatterSeriesDimension = (): Dimension<'props', string, ChartDataRow<string>> => ({
  id: COPILOT_SCATTER_SERIES_FIELD,
  label: 'Series',
  type: 'string',
  descriptions: {
    [COPILOT_SCATTER_SERIES_VALUE]: {
      label: COPILOT_SCATTER_SERIES_VALUE
    }
  }
})

/** ChartCard dimension for forecast history vs forecast series styling. */
export const buildForecastSeriesDimension = (): Dimension<'props', string, ChartDataRow<string>> => ({
  id: COPILOT_FORECAST_SERIES_FIELD,
  label: 'Series',
  type: 'string',
  descriptions: {
    history: {
      label: 'History',
      pattern: 'continuous'
    },
    forecast: {
      label: 'Forecast',
      pattern: 'dash'
    }
  }
})

/** Turns `active_users` into `Active Users` for axis / legend labels. */
export const humanizeFieldLabel = (field: string): string =>
  field.replaceAll('_', ' ').replace(/\b\w/g, (character) => character.toUpperCase())

/** True when a cell looks like an ISO or compact calendar date. */
export const isTemporalFieldValue = (value: unknown): boolean => {
  if (value == null) return false
  const text = String(value)
  return DATE_ISO_PATTERN.test(text) || DATE_COMPACT_PATTERN.test(text)
}

/**
 * True when the first non-null sample of `field` in `data` looks temporal.
 * Used to choose date axes vs categorical values axes.
 */
export const isTemporalField = (field: string, data: Array<Record<string, unknown>>): boolean => {
  const sample = data.find((row) => row[field] != null)?.[field]
  return isTemporalFieldValue(sample)
}

const fieldHintsMatch = (normalizedField: string, hints: readonly string[]): boolean =>
  hints.some((hint) => normalizedField.includes(hint))

/**
 * Best-effort ChartCard metric type from field name + sample values.
 * Prefers name heuristics (revenue/cost → currency; rate/share → percentage).
 */
const inferMetricType = (field: string, data: Array<Record<string, unknown>>): ChartCardMetricType => {
  const normalizedField = field.toLowerCase()
  if (fieldHintsMatch(normalizedField, CURRENCY_FIELD_HINTS)) {
    return 'currency'
  }

  if (fieldHintsMatch(normalizedField, PERCENTAGE_FIELD_HINTS) || normalizedField.endsWith('_pct')) {
    return 'percentage'
  }

  const sampleValues = data
    .map((row) => row[field])
    .filter((value) => value != null)
    .slice(0, 20)

  const looksLikePercent = sampleValues.every((value) => {
    const numeric = Number(value)
    return Number.isFinite(numeric) && numeric >= 0 && numeric <= 1
  })

  return looksLikePercent && normalizedField.includes('percent') ? 'percentage' : 'number'
}

/** Builds ChartCard dimension value labels from distinct cell values in the series. */
export const buildDimensionDescriptions = (
  field: string,
  data: Array<Record<string, unknown>>
): Dimension<'props', string, ChartDataRow<string>>['descriptions'] => {
  const uniqueValues = [
    ...new Set(
      data
        .map((row) => row[field])
        .filter((value) => value != null)
        .map((value) => String(value))
    )
  ]

  if (uniqueValues.length === 0) {
    return undefined
  }

  return Object.fromEntries(uniqueValues.map((value) => [value, { label: value }]))
}

/**
 * ChartCard dimension for an agent field: `date` when values look temporal,
 * otherwise a string dimension with per-value labels.
 */
export const buildAgentDimension = (
  field: string,
  data: Array<Record<string, unknown>>
): Dimension<'props', string, ChartDataRow<string>> => {
  const label = humanizeFieldLabel(field)

  if (isTemporalField(field, data)) {
    return {
      id: field,
      label,
      type: 'date',
      format: 'MMM d'
    }
  }

  return {
    id: field,
    label,
    type: 'string',
    descriptions: buildDimensionDescriptions(field, data)
  }
}

/** ChartCard metric of a known type, with the aggregation that type uses. */
export const buildTypedMetric = (
  { id, label, type }: { id: string; label: string; type: ChartCardMetricType },
  currencyCode?: string
): Metric<string, ChartDataRow<string>> => {
  switch (type) {
    case 'currency':
      return { id, label, type, aggregation: 'sum', ...(currencyCode != null ? { currencyCode } : {}) }
    case 'percentage':
      return { id, label, type, aggregation: 'average' }
    case 'number':
      return { id, label, type, aggregation: 'sum' }
  }
}

/**
 * ChartCard metric for an agent numeric field, with inferred type and aggregation.
 */
export const buildAgentMetric = (
  field: string,
  data: Array<Record<string, unknown>>,
  options?: { currencyCode?: string }
): Metric<string, ChartDataRow<string>> =>
  buildTypedMetric(
    { id: field, label: humanizeFieldLabel(field), type: inferMetricType(field, data) },
    options?.currencyCode
  )

const coerceNumericField = (value: unknown): number | null => {
  const numeric = Number(value)
  return Number.isFinite(numeric) ? numeric : null
}

/**
 * Normalizes agent rows for ChartCard consumption:
 * - coerces `yField` (and scatter `xField`) to finite numbers or null
 * - converts temporal X values to ISO dates
 * - stringifies series/color fields
 * - optionally injects a synthetic scatter series column
 */
export const normalizeAgentChartRows = (
  data: Array<Record<string, unknown>>,
  xField: string,
  yField: string,
  options?: {
    chartType?: CopilotChartType
    colorField?: string
    injectScatterSeries?: boolean
  }
): Array<ChartDataRow<string>> =>
  data.map((row) => {
    const normalizedRow: ChartDataRow<string> = {}

    for (const [key, value] of Object.entries(row)) {
      normalizedRow[key] = value as ChartDataValue
    }

    normalizedRow[yField] = coerceNumericField(row[yField])

    const xValue = row[xField]
    if (options?.chartType === 'scatter') {
      normalizedRow[xField] = coerceNumericField(xValue)
    } else if (xValue != null && isTemporalField(xField, data)) {
      normalizedRow[xField] = normalizeAgentDateValue(xValue)
    }

    const colorField = options?.colorField
    if (colorField != null && colorField.length > 0 && row[colorField] != null) {
      normalizedRow[colorField] = String(row[colorField])
    }

    if (options?.injectScatterSeries === true) {
      normalizedRow[COPILOT_SCATTER_SERIES_FIELD] = COPILOT_SCATTER_SERIES_VALUE
    }

    return normalizedRow
  })
